<template>
  <div class="ccr">
    <div>
      <div v-if="signed_in && pws_id_signed_in">
        <b-alert :show="has_ccr" variant="info">
          It looks like you have already uploaded a CCR file.
          Make sure to <b-button size="sm" variant="outline-primary" @click="changeTab(2)">Sign the
            Certification</b-button>
          and <b-button size="sm" variant="outline-primary" @click="changeTab(3)">Check your CCR Status</b-button>.
        </b-alert>
        
        <!-- Clear process overview -->
        <h5 class="mb-3">CCR Submission Process</h5>
        <p class="mb-4">
          To submit your Consumer Confidence Report (CCR), you can either <strong>upload a file</strong> from your computer 
          or <strong>provide a URL</strong> to your CCR if it's already published online. After submitting your CCR, 
          you'll need to sign the certification to complete the process.
        </p>

        <!-- Critical requirements upfront -->
        <b-alert show variant="warning" class="mb-4">
          <h6 class="mb-2">
            <b-icon icon="exclamation-triangle-fill" aria-hidden="true"></b-icon>
            Important Requirements:
          </h6>
          <ul class="mb-0">
            <li><b>Only upload your CCR</b> - Do NOT upload certifications, proof of notice, or other documents</li>
            <li><b>Accepted formats:</b> PDF (preferred), TIFF, or JPEG</li>
            <li><b>Original PDFs preferred</b> - Avoid scans and photos when possible</li>
          </ul>
        </b-alert>

        <!-- Step 1: Verify Water System -->
        <b-card class="mb-4">
          <template #header>
            <h6 class="mb-0">Step 1: Verify Water System</h6>
          </template>
          <b-form-checkbox id="verify-pws" v-model="verify_pws" size="lg">
            I am submitting for <b>{{ current_pws_label }}</b>
            &nbsp; <b-link to="/change-water-system">Change Water System</b-link>
          </b-form-checkbox>
        </b-card>

        <!-- Step 2 & 3: Only show if water system is verified -->
        <div v-if="verify_pws">
          <!-- Step 2: Choose submission method -->
          <b-card class="mb-4">
            <template #header>
              <h6 class="mb-0">Step 2: Choose Submission Method</h6>
            </template>
            <b-form-radio-group v-model="submission_method" button-variant="outline-primary" buttons size="lg">
              <b-form-radio value="file">
                <b-icon icon="file-earmark-arrow-up"></b-icon>
                Upload CCR File
              </b-form-radio>
              <b-form-radio value="url">
                <b-icon icon="link-45deg"></b-icon>
                Provide CCR URL
              </b-form-radio>
            </b-form-radio-group>
          </b-card>

          <!-- Step 3: Submission details based on method -->
          <b-card class="mb-4">
            <template #header>
              <h6 class="mb-0">Step 3: {{ submission_method === 'file' ? 'Upload Your CCR' : 'Provide CCR URL' }}</h6>
            </template>

            <!-- File Upload Option -->
            <div v-if="submission_method === 'file'">
              <b-alert show variant="info" class="mb-3">
                <ul class="mb-0">
                  <li><b>Accepted formats:</b> PDF (preferred), TIFF, or JPEG</li>
                  <li><b>Original PDFs preferred</b> - Avoid scans and photos when possible</li>
                </ul>
              </b-alert>
              
              <b-form-group label="Select a PDF, TIFF, or JPEG file to upload:" label-for="ccr-upload">
                <b-form-file id="ccr-upload" accept="*.pdf,application/pdf,image/tiff,image/jpeg"
                  v-model="ccr_upload_file"></b-form-file>
              </b-form-group>

              <!-- Help section for file uploads -->
              <details class="mb-3">
                <summary>Need help with file formats?</summary>
                <p class="mt-2 mb-0">
                  If you have a Word document (doc or docx), 
                  <a href="https://support.microsoft.com/en-us/office/export-word-document-as-pdf-4e89b30d-9d7d-4866-af77-3af5536b974c"
                    target="_blank">learn how to convert it to PDF</a>.
                </p>
              </details>
            </div>

            <!-- URL Option -->
            <div v-else>
              <b-alert show variant="info" class="mb-3">
                <ul class="mb-0">
                  <li>Enter a direct link to your CCR PDF document</li>
                  <li>The PDF must be publicly accessible</li>
                  <li>Only the URL will be submitted, not the document itself</li>
                </ul>
              </b-alert>
              
              <b-alert show variant="warning" class="mb-3">
                <b-icon icon="exclamation-triangle-fill"></b-icon>
                <strong>Important:</strong> The URL must link directly to your CCR PDF. If the content is not a valid CCR document, it will be rejected during review.
              </b-alert>
              
              <b-form-group label="Enter CCR PDF URL:" label-for="ccr-url">
                <b-form-input 
                  id="ccr-url" 
                  v-model="ccr_url" 
                  type="url" 
                  placeholder="https://example.com/ccr-report.pdf">
                </b-form-input>
              </b-form-group>

              <!-- PDF Preview -->
              <div v-if="ccr_url && isValidUrl(ccr_url)" class="mb-3">
                <h6>Preview:</h6>
                <p class="text-muted small">Please verify this displays your CCR document:</p>
                <iframe :src="ccr_url" width="100%" height="500" class="border"></iframe>
              </div>
            </div>

            <!-- Submit Button -->
            <b-button 
              :disabled="!canSubmit || uploading" 
              @click="onUploadCCRFile" 
              variant="primary"
              size="lg"
              block>
              <b-spinner small v-if="uploading"></b-spinner>
              {{ submission_method === 'file' ? 'Upload CCR' : 'Submit CCR URL' }}
            </b-button>
          </b-card>
        </div>
        <div class="mt-3">
          <b-alert fade dismissible variant="success" v-model="upload_success_alert">
            <div class="mb-2">
              {{ submission_method === 'file' ? 'File' : 'URL' }} <u>{{ ccr_upload_file_name }}</u> submitted successfully. Thank you!
            </div>
            <div>
              <strong>Next Step:</strong> After your CCR distribution is complete, make sure to 
              <b-button size="sm" variant="outline-success" @click="changeTab(2)">
                <b-icon icon="check-circle"></b-icon>
                Certify CCR Distribution
              </b-button>
            </div>
          </b-alert>
          <b-alert fade dismissible variant="danger" v-model="upload_failure_alert">
            Submission failed. <b>Details</b>: <span>{{ upload_failure_message }}</span>
          </b-alert>
        </div>
      </div>
      <div v-else class="mt-5">
        <b-alert show variant="info">
          You must be <b-link to="signin">signed in </b-link>to <b>upload CCR files</b>.
        </b-alert>
      </div>
    </div>
  </div>
</template>
<script>

export default {
  name: "CCRUploads",
  data() {
    return {
      verify_pws: false,
      submission_method: 'file', // 'file' or 'url'
      ccr_upload_file: null,
      ccr_upload_file_name: '',
      ccr_url: '',
      url_validation_status: '', // 'checking', 'valid', 'invalid', ''
      url_validation_message: '',
      upload_success_alert: false,
      upload_failure_alert: false,
      upload_failure_message: '',
      uploading: false,
      has_ccr: false,
    }
  },
  methods: {
    isValidUrl(string) {
      try {
        const url = new URL(string)
        return url.protocol === 'http:' || url.protocol === 'https:'
      } catch (_) {
        return false
      }
    },
    onUploadCCRFile() {
      const form_data = new FormData()
      form_data.append('pws_id', this.pws_id_signed_in)
      
      if (this.submission_method === 'file') {
        // File upload
        this.ccr_upload_file_name = this.ccr_upload_file.name
        form_data.append('ccr_file', this.ccr_upload_file)
      } else {
        // URL submission
        this.ccr_upload_file_name = this.ccr_url
        form_data.append('ccr_url', this.ccr_url)
      }
      
      this.uploading = true
      this.axios.post(process.env.VUE_APP_API_URI + '/ccr-file', form_data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then((res) => {
        console.log(res)
        if (res.data && res.data.msg == 'OK') {
          this.upload_success_alert = true
          if (this.submission_method === 'file') {
            this.ccr_upload_file = null
          } else {
            this.ccr_url = ''
          }
          this.has_ccr = true
        } else {
          this.upload_failure_alert = true
          this.upload_failure_message = res.data.msg || 'No details.'
        }
      }).catch((error) => {
        console.error(error)
        this.upload_failure_message = 'No details.'
        this.upload_failure_alert = true
      }).finally(() => {
        this.uploading = false
      })
    },
    changeTab(tabIndex) {
      this.$emit('changeTab', tabIndex)
    },
    fetchCCRStatus() {
      const url = process.env.VUE_APP_API_URI + '/ccr-status?pws_id=' + this.pws_id_signed_in
      this.axios.get(url).then((res) => {
        console.log(res)
        if (res.data && res.data.status_data) {
          // look for has_ccr
          for (const doc of res.data.status_data) {
            if (doc.has_ccr) {
              this.has_ccr = true
            }
          }
        }
      }).catch((error) => {
        console.error(error)
      })
    },
  },
  mounted() {
    this.fetchCCRStatus()
  },
  computed: {
    pws_id_signed_in() {
      return this.$store.state.pws_id
    },
    current_pws() {
      return this.$store.state.pws
    },
    current_pws_label() {
      return `${this.current_pws.pws_name} (${this.current_pws.pws_id})`
    },
    signed_in() {
      return this.$store.state.signed_in
    },
    canSubmit() {
      if (!this.verify_pws) return false
      if (this.submission_method === 'file') {
        return !!this.ccr_upload_file
      } else {
        return this.ccr_url && this.isValidUrl(this.ccr_url)
      }
    }
  }
}
</script>
